import { RecalProcessAndGenSearchCriteria } from "domain/entity/Recalculation/RecalProcessAndGenSearchCriteria";
import { recalStateDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { RecalProcessAndGenConstant } from "presentation/constant/Recalculation/RecalProcessAndGenConstant";
import { useRecalProcessAndGenVM } from "presentation/hook/Recalculation/useRecalProcessAndGenVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useRecalProcessAndGenMaintenanceTracked } from "presentation/store/Recalculation/RecalProcessAndGenMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import NumberInputComponent from "presentation/view/components/NumberInputComponent";
import { useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, HPHInputMask, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

export const RecalProcessAndGenSearchPanel = () => {
    const SCREEN_CONSTANT = RecalProcessAndGenConstant;
    const [recalProcessAndGenState] = useRecalProcessAndGenMaintenanceTracked();
    const recalProcessAndGenVM = useRecalProcessAndGenVM();
    const { searchCriteria } = recalProcessAndGenState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();

    const resetButtonClicked = useCallback(async () => {
        recalProcessAndGenVM.onSearchCriteriaResetClick();
    }, [recalProcessAndGenVM]);

    const searchButtonClicked = useCallback(async (searchCriteria: RecalProcessAndGenSearchCriteria) => {
        if (Object.values(searchCriteria).every(val => val == null || val === "")) {
            messageBarVM.showError("Please input any criteria to search.");
            return;
        }
        setIsLoading(true);
        recalProcessAndGenVM.onSearch(searchCriteria).then(() => {
            setIsLoading(false);
        })
    }, [messageBarVM, recalProcessAndGenVM]);

    const memoSubChgTypeOptions = useMemo(() => {
        if (!searchCriteria.chargeType) {
            return Object.values(recalProcessAndGenState.dynamicOptions.subChargeTypeDropdownOptions).flat();
        }
        return recalProcessAndGenState.dynamicOptions.subChargeTypeDropdownOptions[searchCriteria.chargeType];
    }, [searchCriteria.chargeType, recalProcessAndGenState.dynamicOptions.subChargeTypeDropdownOptions])

    const memoTariffCodeOptions = useMemo(() => {
        if (!searchCriteria.tarType) {
            return Object.values(recalProcessAndGenState.dynamicOptions.tariffCodeDropdownOptions).flat();
        }
        return recalProcessAndGenState.dynamicOptions.tariffCodeDropdownOptions[searchCriteria.tarType];
    }, [searchCriteria.tarType, recalProcessAndGenState.dynamicOptions.tariffCodeDropdownOptions]);

    const onNumberFieldChange = useCallback((e: any, fieldName: string, fieldValue: any) => {
        recalProcessAndGenVM.onSearchFieldChange(fieldName, fieldValue);
    }, [recalProcessAndGenVM]);

    const memoTariffType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.tarType || ''}
                fieldValue={searchCriteria?.tarType}
                fieldLabel={SCREEN_CONSTANT.TARIFF_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'tarType'}
                options={recalProcessAndGenState.dynamicOptions.tariffTypeDropdownOptions}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.TARIFF_TYPE, recalProcessAndGenState.dynamicOptions.tariffTypeDropdownOptions, recalProcessAndGenVM, searchCriteria?.tarType])

    const memoTariffCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.tarCode || ''}
                fieldValue={searchCriteria?.tarCode}
                fieldLabel={SCREEN_CONSTANT.TARIFF_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'tarCode'}
                options={memoTariffCodeOptions}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.TARIFF_CODE, memoTariffCodeOptions, recalProcessAndGenVM, searchCriteria?.tarCode])

    const memoChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.chargeType || ''}
                fieldValue={searchCriteria?.chargeType}
                fieldLabel={SCREEN_CONSTANT.CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeType'}
                maxLength={60}
                options={recalProcessAndGenState.dynamicOptions.chargeTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.CHARGE_TYPE, recalProcessAndGenState.dynamicOptions.chargeTypeDropdownOptions, recalProcessAndGenVM, searchCriteria?.chargeType])

    const memoSubChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.subChargeType || ''}
                fieldValue={searchCriteria?.subChargeType}
                fieldLabel={SCREEN_CONSTANT.SUB_CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subChargeType'}
                maxLength={60}
                options={memoSubChgTypeOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.SUB_CHARGE_TYPE, memoSubChgTypeOptions, recalProcessAndGenVM, searchCriteria?.subChargeType])


    const memoBillToCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.billToCompany ?? ''}
                fieldValue={searchCriteria?.billToCompany}
                fieldLabel={SCREEN_CONSTANT.BILLTO_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompany'}
                maxLength={60}
                isLimitOptions={true}
                options={recalProcessAndGenState.dynamicOptions.companyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.billToCompany, SCREEN_CONSTANT.BILLTO_COMPANY, recalProcessAndGenState.dynamicOptions.companyCodeDropdownOptions, recalProcessAndGenVM])

    const memoCustomerCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.customerCode ?? ''}
                fieldValue={searchCriteria?.customerCode}
                fieldLabel={SCREEN_CONSTANT.CUSTOMER_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'customerCode'}
                maxLength={60}
                isLimitOptions={true}
                options={recalProcessAndGenState.dynamicOptions.customerCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.customerCode, SCREEN_CONSTANT.CUSTOMER_CODE, recalProcessAndGenState.dynamicOptions.customerCodeDropdownOptions, recalProcessAndGenVM])


    const memoState = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.state ?? ''}
                fieldValue={searchCriteria?.state}
                fieldLabel={SCREEN_CONSTANT.STATE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'state'}
                maxLength={60}
                isLimitOptions={true}
                options={recalStateDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.state, SCREEN_CONSTANT.STATE, recalProcessAndGenVM])

    const memoDocDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.DOC_DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "docDateFrom", endField: "docDateTo" }}
                dateRange={{ startDate: searchCriteria.docDateFrom, endDate: searchCriteria.docDateTo }}
                onDatesChange={recalProcessAndGenVM.onSearchDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.DOC_DATE_RANGE, recalProcessAndGenVM.onSearchDateRangeChange, searchCriteria.docDateFrom, searchCriteria.docDateTo])

    const memoDepDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.DEP_DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "depDateFrom", endField: "depDateTo" }}
                dateRange={{ startDate: searchCriteria.depDateFrom, endDate: searchCriteria.depDateTo }}
                onDatesChange={recalProcessAndGenVM.onSearchDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.DEP_DATE_RANGE, recalProcessAndGenVM.onSearchDateRangeChange, searchCriteria.depDateFrom, searchCriteria.depDateTo])

    const memoOpsDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.OPS_DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "opsDateFrom", endField: "opsDateTo" }}
                dateRange={{ startDate: searchCriteria.opsDateFrom, endDate: searchCriteria.opsDateTo }}
                onDatesChange={recalProcessAndGenVM.onSearchDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.OPS_DATE_RANGE, recalProcessAndGenVM.onSearchDateRangeChange, searchCriteria.opsDateFrom, searchCriteria.opsDateTo])

    const memoGroupNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.GROUP_NO}
                maxLength={10}
                fieldName={"groupNo"}
                value={searchCriteria?.groupNo ?? ''}
                errorMessage={''}
                disabled={false}
                decimalPlaces={0}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.GROUP_NO, searchCriteria?.groupNo, onNumberFieldChange])

    const memoSeqNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.SEQ_NO}
                maxLength={10}
                fieldName={"seqNo"}
                value={searchCriteria?.seqNo ?? ''}
                errorMessage={''}
                disabled={false}
                decimalPlaces={0}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.SEQ_NO, searchCriteria?.seqNo, onNumberFieldChange])

    const memoDocNoFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.docNoFrom ?? ''}
                fieldValue={searchCriteria?.docNoFrom}
                fieldLabel={SCREEN_CONSTANT.DOC_NO_FROM}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'docNoFrom'}
                maxLength={12}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.docNoFrom, SCREEN_CONSTANT.DOC_NO_FROM, recalProcessAndGenVM])

    const memoDocNoTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.docNoTo ?? ''}
                fieldValue={searchCriteria?.docNoTo}
                fieldLabel={SCREEN_CONSTANT.DOC_NO_TO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'docNoTo'}
                maxLength={12}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.docNoTo, SCREEN_CONSTANT.DOC_NO_TO, recalProcessAndGenVM])

    const memoNtnNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.ntnNo ?? ''}
                fieldValue={searchCriteria?.ntnNo}
                fieldLabel={SCREEN_CONSTANT.NTN_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'ntnNo'}
                maxLength={30}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.ntnNo, SCREEN_CONSTANT.NTN_NO, recalProcessAndGenVM])

    const memoCoVslVoy = useMemo(() =>
        <div className='flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <HPHInputMask
                width='470px'
                label={SCREEN_CONSTANT.CO_VSL_VOY}
                autoFocus={false}
                value={{
                    co: searchCriteria.co ?? '',
                    vsl: searchCriteria.vsl ?? '',
                    voy: searchCriteria.voy ?? ''
                }}
                fieldConfiguration={{
                    co: { maxLength: 4, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    vsl: { maxLength: 6, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    voy: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                }}
                onDataChange={(e: any) => recalProcessAndGenVM.onSearchCoVslVoyChange(e, {
                    co: 'co',
                    vsl: 'vsl',
                    voy: 'voy'
                })} />
        </div>
        , [SCREEN_CONSTANT.CO_VSL_VOY, searchCriteria.co, searchCriteria.vsl, searchCriteria.voy, recalProcessAndGenVM])

    return (
        <div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px" }}>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{SCREEN_CONSTANT.SEARCH_CRITERIA}</SidebarTitle>
                        <SidebarActionCross>
                            <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={recalProcessAndGenVM.onSearchClick} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>

            {isLoading && <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ maxHeight: '75vh', height: '75vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>

                        <CriteriaItemContainer>
                            {memoDocNoFrom}
                            {memoDocNoTo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoNtnNo}
                            {memoState}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoGroupNo}
                            {memoSeqNo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoBillToCompany}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCustomerCode}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoChargeType}
                            {memoSubChargeType}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoTariffType}
                            {memoTariffCode}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoDocDateFromTo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoOpsDateFromTo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoDepDateFromTo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCoVslVoy}
                        </CriteriaItemContainer>

                    </div>
                </div>
            </div>
            <SidebarActionBar>
                <HPHButton label={'Reset'} size={'Small'} theme={'Secondary'} onClick={resetButtonClicked} />
                <HPHButton label={'Search'} size={'Small'} theme={'Primary'} onClick={() => searchButtonClicked(searchCriteria)} />
            </SidebarActionBar>
        </div>
    );
}